import React from 'react';
import { Link } from 'react-router-dom';

/**
 * بطاقة إعلان أفقية موحّدة — مطابقة لتصميم الويب (Web-Market).
 * تُستخدم في السوق، التصنيفات، ملف البائع، المفضلة.
 */

export interface CompactAd {
  id: number;
  title: string;
  price?: number | string | null;
  price_type?: string | null;
  city?: { id?: number; name_ar: string } | null;
  category?: { name_ar: string } | null;
  gender?: string | null;
  weight?: number | string | null;
  age_months?: number | null;
  images?: { image_url: string; thumbnail_url: string | null; is_primary?: boolean }[];
  views_count?: number;
  is_featured?: boolean;
  is_promoted?: boolean;
  created_at?: string;
  published_at?: string;
  user?: { id?: number; name?: string; avatar?: string | null; is_verified?: boolean } | null;
}

const genderMap: Record<string, string> = { male: 'ذكر', female: 'أنثى', mixed: 'مختلط' };

const timeAgo = (d?: string): string => {
  if (!d) return '';
  const s = Math.floor((Date.now() - new Date(d).getTime()) / 1000);
  if (s < 3600) return 'منذ ' + Math.max(1, Math.floor(s / 60)) + ' د';
  if (s < 86400) return 'منذ ' + Math.floor(s / 3600) + ' س';
  if (s < 2592000) return 'منذ ' + Math.floor(s / 86400) + ' يوم';
  return 'منذ ' + Math.floor(s / 2592000) + ' شهر';
};

interface Props {
  ad: CompactAd;
  onFav?: (id: number) => void;
  favActive?: boolean;
}

const sheepIcon = (
  <svg width="34" height="34" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true"><circle cx="6" cy="9" r="2" /><circle cx="10" cy="5.5" r="2" /><circle cx="14" cy="5.5" r="2" /><circle cx="18" cy="9" r="2" /><path d="M12 11c-3 0-6 3.5-6 6.5 0 2 1.5 3 3 3s2-.8 3-.8 1.5.8 3 .8 3-1 3-3c0-3-3-6.5-6-6.5z" /></svg>
);

const AdCardCompact: React.FC<Props> = ({ ad, onFav, favActive }) => {
  const img = ad.images?.find((i) => i.is_primary) || ad.images?.[0];
  const src = img?.thumbnail_url || img?.image_url;
  const feat = Boolean(ad.is_featured || ad.is_promoted);
  const priceNum = typeof ad.price === 'string' ? parseFloat(ad.price) : ad.price;
  const priceLabel =
    ad.price_type === 'negotiable' ? 'قابل للتفاوض' : ad.price_type === 'auction' ? 'مزاد' : '';
  const when = ad.published_at || ad.created_at;
  const seller = ad.user;

  return (
    <article style={{
      position: 'relative', background: '#FFFFFF',
      border: feat ? '1.5px solid #B8893C' : '1px solid #E6ECE9',
      borderRadius: 18, padding: 12, display: 'flex', flexWrap: 'wrap', gap: 16,
    }}>
      <Link to={'/ads/' + ad.id} aria-label={ad.title} style={{
        position: 'relative', flex: '0 0 160px', height: 136, borderRadius: 14,
        background: src ? '#F1F4F2' : 'linear-gradient(135deg, #E7F2EE, #CFE4DB)',
        color: '#7FA898', display: 'flex', alignItems: 'center', justifyContent: 'center', overflow: 'hidden',
      }}>
        {src ? <img src={src} alt={ad.title} loading="lazy" style={{ width: '100%', height: '100%', objectFit: 'contain' }} /> : sheepIcon}
        {feat && <span style={{ position: 'absolute', top: 8, right: 8, fontSize: 11.5, fontWeight: 700, padding: '2px 9px', borderRadius: 7, background: '#B8893C', color: '#FFFFFF' }}>مميز</span>}
        {typeof ad.views_count === 'number' && (
          <span style={{ position: 'absolute', bottom: 8, left: 8, height: 22, padding: '0 7px', borderRadius: 7, background: 'rgba(23,36,31,0.6)', color: '#FFFFFF', fontSize: 11.5, fontWeight: 700, display: 'flex', alignItems: 'center', gap: 4 }}>
            <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><path d="M2 12s3.5-7 10-7 10 7 10 7-3.5 7-10 7S2 12 2 12zM12 9a3 3 0 1 0 0 6 3 3 0 0 0 0-6z" /></svg>
            {ad.views_count}
          </span>
        )}
      </Link>

      <div style={{ flex: '1 1 260px', minWidth: 0, display: 'flex', flexDirection: 'column', gap: 7, padding: '2px 0 2px 44px' }}>
        <Link to={'/ads/' + ad.id} style={{ fontFamily: "'Cairo', sans-serif", fontSize: 15.5, fontWeight: 700, lineHeight: 1.4, color: '#17241F', textDecoration: 'none' }}>{ad.title}</Link>

        {(ad.category || ad.gender || ad.weight || ad.age_months) && (
          <span style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
            {ad.category && <span style={{ height: 26, padding: '0 10px', borderRadius: 999, background: '#E7F2EE', color: '#005343', fontSize: 12.5, fontWeight: 600, display: 'flex', alignItems: 'center' }}>{ad.category.name_ar}</span>}
            {ad.gender && <span style={{ height: 26, padding: '0 10px', borderRadius: 999, background: '#F1F4F2', color: '#3E4C46', fontSize: 12.5, fontWeight: 600, display: 'flex', alignItems: 'center' }}>{genderMap[ad.gender] || ad.gender}</span>}
            {ad.weight ? <span style={{ height: 26, padding: '0 10px', borderRadius: 999, background: '#F1F4F2', color: '#3E4C46', fontSize: 12.5, fontWeight: 600, display: 'flex', alignItems: 'center' }}>{ad.weight} كجم</span> : null}
            {ad.age_months ? <span style={{ height: 26, padding: '0 10px', borderRadius: 999, background: '#F1F4F2', color: '#3E4C46', fontSize: 12.5, fontWeight: 600, display: 'flex', alignItems: 'center' }}>{ad.age_months} شهر</span> : null}
          </span>
        )}

        <span style={{ fontFamily: "'Cairo', sans-serif", fontSize: 18, fontWeight: 800, color: '#005343', lineHeight: 1.3 }}>
          {Number.isFinite(priceNum as number) ? Number(priceNum).toLocaleString() : (ad.price ?? '')}{' '}
          <span style={{ fontFamily: "'Tajawal', sans-serif", fontSize: 12, fontWeight: 500, color: '#6E7C76' }}>ر.س{priceLabel ? ' · ' + priceLabel : ''}</span>
        </span>

        <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: '6px 14px', fontSize: 13, color: '#6E7C76' }}>
          {seller?.name && (
            <Link to={seller.id ? '/users/' + seller.id : '#'} style={{ display: 'flex', alignItems: 'center', gap: 6, color: '#17241F', fontWeight: 700, textDecoration: 'none' }}>
              <span style={{ width: 24, height: 24, borderRadius: '50%', background: '#E7F2EE', color: '#005343', fontSize: 11, display: 'flex', alignItems: 'center', justifyContent: 'center', overflow: 'hidden' }}>
                {seller.avatar ? <img src={seller.avatar} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover' }} /> : (seller.name.charAt(0) || '؟')}
              </span>
              {seller.name}
              {seller.is_verified && (
                <span aria-label="موثّق" style={{ width: 15, height: 15, borderRadius: '50%', background: '#2F7DD1', color: '#FFFFFF', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                  <svg width="9" height="9" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><path d="M5 12l5 5L20 7" /></svg>
                </span>
              )}
            </Link>
          )}
          {ad.city && (
            <Link to={ad.city.id ? '/ads?city_id=' + ad.city.id : '/ads'} style={{ display: 'flex', alignItems: 'center', gap: 4, color: '#005343', fontWeight: 600, textDecoration: 'underline', textUnderlineOffset: 3, textDecorationColor: '#CFE0DA' }}>
              <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><path d="M12 21s-7-6.2-7-11a7 7 0 0 1 14 0c0 4.8-7 11-7 11zM12 8a2 2 0 1 0 0 4 2 2 0 0 0 0-4z" /></svg>
              {ad.city.name_ar}
            </Link>
          )}
          {when && (
            <span style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
              <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><path d="M12 3a9 9 0 1 0 0 18 9 9 0 0 0 0-18zM12 7v5l3 2" /></svg>
              {timeAgo(when)}
            </span>
          )}
        </div>
      </div>

      <button type="button" aria-label={favActive ? 'إزالة من المفضلة' : 'أضف للمفضلة'} onClick={(e) => { e.preventDefault(); e.stopPropagation(); onFav?.(ad.id); }}
        style={{ position: 'absolute', top: 14, left: 14, width: 36, height: 36, borderRadius: '50%', border: '1px solid #E6ECE9', background: favActive ? '#FDECEC' : '#FFFFFF', color: favActive ? '#E53935' : '#005343', display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer', padding: 0 }}>
        <svg width="17" height="17" viewBox="0 0 24 24" fill={favActive ? 'currentColor' : 'none'} stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><path d="M20.8 4.6a5.5 5.5 0 0 0-7.8 0L12 5.7l-1-1.1a5.5 5.5 0 0 0-7.8 7.8l1 1.1L12 21l7.8-7.5 1-1.1a5.5 5.5 0 0 0 0-7.8z" /></svg>
      </button>
    </article>
  );
};

export default AdCardCompact;
